import { useState, useEffect, useRef, FunctionComponent } from "react"
import { useDispatch } from "react-redux"
import { NavLink } from "react-router-dom"
import type { ThunkDispatch } from "redux-thunk"
import type { AnyAction } from "redux"
import { IconSettings, IconCompanySettings } from "@rhumbix/rmbx_design_system_web"
import * as API from "../../api"
import { switchCompany } from "../../actions/auth"
import UserInfo from "./user-info"
import { getFlagEnabled } from "../../getFlagValue"

import "company-switcher.less"
import { tUserRole } from "../../common/types"

/** Shape returned by GET /api/v4/user_employees/ */
interface UserEmployee {
    id: number
    first_name: string
    last_name: string
    thumbnail: string | null
    company_name: string
    company_logo: string | null
    last_active: string | null
    email: string | null
    user_role: tUserRole
}

type tProps = {
    /** PK of the currently active employee — used to mark the active row. */
    currentEmployeeId: number
    isSidebarCollapsed: boolean
    /** The current user's role — used to determine admin-only items. */
    user_role: tUserRole
    email: string
}

const USER_ROLE_LABELS: Record<tUserRole, string> = {
    ADMIN: "Administrator",
    PAYROLL_ADMIN: "Payroll Admin",
    PM: "Project Manager",
    FOREMAN: "Foreman",
    WORKER: "Worker",
    OFFICE_STAFF: "Office Staff",
    GUEST: "Guest",
    GUEST_SIGNATURE: "Guest Signature",
}

const formatRole = (role: tUserRole): string => USER_ROLE_LABELS[role] ?? role

/**
 * Company-switcher trigger and floating pop-out panel.
 *
 * Renders a trigger row in the sidebar ("Your Organizations") that opens a
 * pop-out panel positioned to the right of the sidebar. The panel lists all
 * companies the user belongs to with per-company email and role, and includes
 * an admin-only "Manage Companies" link at the bottom.
 *
 * Renders nothing when the sidebar is collapsed. When the user has only one
 * company and is not an admin who can manage companies, the trigger is also
 * hidden.
 */
const CompanySwitcher: FunctionComponent<tProps> = ({ currentEmployeeId, isSidebarCollapsed, user_role, email }) => {
    const dispatch = useDispatch<ThunkDispatch<unknown, unknown, AnyAction>>()
    const [employees, setEmployees] = useState<UserEmployee[]>([])
    const [isOpen, setIsOpen] = useState(false)
    const [popoutPos, setPopoutPos] = useState({ top: 0, left: 0 })
    const triggerRef = useRef<HTMLDivElement>(null)
    const popoutRef = useRef<HTMLDivElement>(null)

    // Show "Manage Companies" if the flag is on and the user is an ADMIN on any
    // of their companies (not just the currently active one). The prop-based check
    // is a fast path before the employees fetch returns; the array check picks up
    // the case where a non-current company carries the ADMIN role.
    const canManageCompanies = getFlagEnabled("WA-9395-adsk-licensing") && (user_role === "ADMIN" || employees.some(emp => emp.user_role === "ADMIN"))

    useEffect(() => {
        API.getUserEmployees().then((result: UserEmployee[]) => {
            const sorted = [...result].sort((a, b) => {
                if (!a.last_active) return 1
                if (!b.last_active) return -1
                return new Date(b.last_active).getTime() - new Date(a.last_active).getTime()
            })
            setEmployees(sorted)
        })
    }, [currentEmployeeId])

    // Close on outside click
    useEffect(() => {
        if (!isOpen) return
        const handleOutsideClick = (e: MouseEvent) => {
            const target = e.target as Node
            if (triggerRef.current?.contains(target) || popoutRef.current?.contains(target)) return
            setIsOpen(false)
        }
        document.addEventListener("mousedown", handleOutsideClick)
        return () => document.removeEventListener("mousedown", handleOutsideClick)
    }, [isOpen])

    if (isSidebarCollapsed) return null
    if (employees.length <= 1 && !canManageCompanies) {
        return <UserInfo user_role={user_role} email={email} isSidebarCollapsed={isSidebarCollapsed} />
    }

    const handleTriggerClick = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect()
            setPopoutPos({ top: rect.top, left: rect.right + 8 })
        }
        setIsOpen(v => !v)
    }

    const handleSelect = (employeeId: number) => {
        setIsOpen(false)
        dispatch(switchCompany(employeeId))
    }

    // The first non-current employee in the sorted list is "Last Used"
    const lastUsedId = employees.find(emp => emp.id !== currentEmployeeId)?.id
    const currentEmployee = employees.find(emp => emp.id === currentEmployeeId)
    return (
        <div className="company-switcher-section" ref={triggerRef}>
            <span className="company-switcher-badge company-switcher-badge--current">{currentEmployee?.company_name}</span>
            <button className={`company-switcher-trigger${isOpen ? " open" : ""}`} onClick={handleTriggerClick} aria-expanded={isOpen} aria-haspopup="true">
                <EmployeeAvatar name={`${currentEmployee?.first_name} ${currentEmployee?.last_name}`} logoUrl={currentEmployee?.thumbnail} />
                <span className="company-switcher-header">
                    <UserInfo
                        name={`${currentEmployee?.last_name}, ${currentEmployee?.first_name}`}
                        user_role={user_role}
                        email={email}
                        isSidebarCollapsed={isSidebarCollapsed}
                        style={{ margin: 0, paddingLeft: 8, display: "block", width: "100%" }}
                    />
                </span>
                <span className="company-switcher-chevron" aria-hidden="true">
                    ›
                </span>
            </button>

            {isOpen && (
                <div ref={popoutRef} className="company-switcher-popout" style={{ top: `${popoutPos.top}px`, left: `${popoutPos.left}px` }} role="menu">
                    <ul className="company-switcher-popout-list">
                        {employees.map(emp => {
                            const isCurrent = emp.id === currentEmployeeId
                            const isLastUsed = !isCurrent && emp.id === lastUsedId
                            return (
                                <li key={emp.id} className="company-switcher-popout-row" role="menuitem">
                                    <div className="company-switcher-popout-row-main">
                                        <CompanyAvatar name={emp.company_name} logoUrl={emp.company_logo} />
                                        <div className="company-switcher-popout-info">
                                            <div className="company-switcher-popout-name-row">
                                                <span className="company-switcher-popout-name" title={emp.company_name}>
                                                    {emp.company_name}
                                                </span>
                                                {isCurrent && <span className="company-switcher-badge company-switcher-badge--current">Current</span>}
                                                {isLastUsed && <span className="company-switcher-badge company-switcher-badge--last-used">Last Used</span>}
                                            </div>
                                            {emp.email && <span className="company-switcher-popout-email">{emp.email}</span>}
                                            {emp.user_role && <span className="company-switcher-popout-role">{formatRole(emp.user_role)}</span>}
                                            {!isCurrent && (
                                                <button className="company-switcher-popout-action" onClick={() => handleSelect(emp.id)}>
                                                    Sign In
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            )
                        })}
                    </ul>

                    {canManageCompanies && (
                        <NavLink to="/rhumbix/manage-companies" className="company-switcher-manage-link no-hover-menu-item" onClick={() => setIsOpen(false)}>
                            <IconSettings viewBox="0 0 16 16" width={20} height={20} aria-hidden="true" />
                            <span>Manage Companies</span>
                        </NavLink>
                    )}
                </div>
            )}
        </div>
    )
}

/** 24×24 company logo, falling back to an IconUser avatar. */
const CompanyAvatar: FunctionComponent<{ name: string; logoUrl: string | null }> = ({ name, logoUrl }) => {
    if (logoUrl) {
        return <img src={logoUrl} alt={name} className="company-switcher-avatar" />
    }
    return (
        <span className="company-switcher-avatar company-switcher-avatar--default" aria-hidden="true">
            <IconCompanySettings width={20} height={20} />
        </span>
    )
}

const EmployeeAvatar: FunctionComponent<{ name: string; logoUrl?: string | null }> = ({ name, logoUrl }) => {
    if (logoUrl) {
        return <img src={logoUrl} alt={name} className="company-switcher-avatar" />
    }
    return (
        <span className="company-switcher-avatar company-switcher-avatar--default" aria-hidden="true">
            <img
                alt=""
                className="menu-title-icon"
                data-icon-name="user"
                // eslint-disable-next-line @typescript-eslint/no-require-imports
                src={require("sidebar_icons/user-email-white.svg")}
            />
        </span>
    )
}

export default CompanySwitcher
